<script setup lang="ts">
import { useTranslation } from 'i18next-vue';
import {
  TransactionDetailsDialog,
  type TransactionDetailsDialogData,
  type TransactionDetailsDialogDirection,
  type TransactionDetailsDialogSource,
} from '@peanuds/modules';
import Message from 'primevue/message';
import { ref, watch } from 'vue';

import { useRepeatPaymentNavigation } from '../send-money/repeat-payment/useRepeatPaymentNavigation';
import { ArrowRedoIcon, UiButton } from '@peanuds/ui';

interface Props {
  visible: boolean;
  transactionId?: string | null;
  direction?: TransactionDetailsDialogDirection | null;
  transactionSource?: TransactionDetailsDialogSource | null;
}

interface Emits {
  'update:visible': [value: boolean];
  close: [];
}

const {
  visible,
  transactionId = null,
  direction = null,
  transactionSource = null,
} = defineProps<Props>();
const emit = defineEmits<Emits>();

const { t } = useTranslation();
const { canRepeatPayment, startRepeatPayment } = useRepeatPaymentNavigation();
const isRepeatingPayment = ref(false);
const hasRepeatPaymentFailed = ref(false);

watch(
  () => [visible, transactionId] as const,
  () => {
    hasRepeatPaymentFailed.value = false;
  },
);

const handleUpdateVisible = (value: boolean): void => {
  emit('update:visible', value);
};

const handleClose = (): void => {
  emit('close');
};

const handleRepeatPayment = async (
  dialogData: TransactionDetailsDialogData | null,
  close: () => void,
): Promise<void> => {
  if (dialogData === null || isRepeatingPayment.value) {
    return;
  }

  isRepeatingPayment.value = true;
  hasRepeatPaymentFailed.value = false;

  try {
    const didStartRepeatPayment = await startRepeatPayment(dialogData);
    if (didStartRepeatPayment) {
      close();
      return;
    }

    hasRepeatPaymentFailed.value = true;
  } finally {
    isRepeatingPayment.value = false;
  }
};
</script>

<template>
  <TransactionDetailsDialog
    :visible="visible"
    :transaction-id="transactionId"
    :direction="direction"
    :transaction-source="transactionSource"
    @update:visible="handleUpdateVisible"
    @close="handleClose"
  >
    <template #footer-message>
      <Message
        v-if="hasRepeatPaymentFailed"
        severity="warn"
        size="small"
        data-testid="transaction-details-repeat-payment-error"
        class="money-transaction-details__repeat-error"
      >
        {{ t('transactionDetails.actions.repeatPaymentUnavailable') }}
      </Message>
    </template>

    <template #footer-action="{ dialogData, close }">
      <UiButton
        v-if="canRepeatPayment(dialogData)"
        severity="secondary"
        class="transaction-details__footer-btn"
        data-testid="transaction-details-repeat-payment"
        :label="t('transactionDetails.actions.repeatPayment')"
        :loading="isRepeatingPayment"
        :disabled="isRepeatingPayment"
        @click="handleRepeatPayment(dialogData, close)"
      >
        <template #icon>
          <ArrowRedoIcon />
        </template>
      </UiButton>
    </template>
  </TransactionDetailsDialog>
</template>

<style scoped lang="scss">
.money-transaction-details__repeat-error {
  flex-shrink: 0;
  border-radius: $radius-16;
  font-size: $text-sm;
}
</style>
